import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  fetchAuthMe,
  setBilling as setAuthBilling,
  fetchTenantBilling,
} from "../../../store/authSlice";
import authAPI from "../../../services/authApi";
import clinicAPI from "../../../services/clinicApi";
import { HubFormCard, HubScreen } from "./HubPwa";
import aa from "./adminApp.module.css";
import { Button, Field, Input, useToast } from "../../ui";
import { TRIAL_DAYS } from "../../../constants/trial";
import {
  AdminPushBlock,
  BillingDetailsCard,
  formatDateRu,
  formatRub,
} from "./staffProfileShared";

export default function AdminOwnerProfileSettings() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const toast = useToast();
  const { profile } = useSelector((state) => state.auth);
  const [form, setForm] = useState({ full_name: "", phone: "" });
  const [pwd, setPwd] = useState({ current: "", next: "" });
  const [billing, setBilling] = useState(null);
  const [saving, setSaving] = useState(false);
  const [pwdSaving, setPwdSaving] = useState(false);
  const [payBusy, setPayBusy] = useState(false);

  const loadBilling = useCallback(async () => {
    try {
      const next = await dispatch(fetchTenantBilling()).unwrap();
      setBilling(next || null);
    } catch {
      setBilling(null);
      dispatch(setAuthBilling(null));
    }
  }, [dispatch]);

  useEffect(() => {
    setForm({
      full_name: profile?.full_name || "",
      phone: profile?.phone || "",
    });
  }, [profile]);

  useEffect(() => {
    loadBilling();
  }, [loadBilling]);

  const saveProfile = async () => {
    setSaving(true);
    try {
      await authAPI.updateMyAccount({
        full_name: form.full_name,
        phone: form.phone,
      });
      await dispatch(fetchAuthMe());
      toast.success("Сохранено");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка сохранения");
    } finally {
      setSaving(false);
    }
  };

  const savePassword = async () => {
    if (!pwd.current || !pwd.next) {
      toast.error("Заполните оба поля пароля");
      return;
    }
    setPwdSaving(true);
    try {
      await authAPI.changePassword(pwd.current, pwd.next);
      setPwd({ current: "", next: "" });
      toast.success("Пароль изменён");
    } catch (e) {
      toast.error(e.response?.data?.error || "Ошибка");
    } finally {
      setPwdSaving(false);
    }
  };

  const paySubscription = async () => {
    setPayBusy(true);
    try {
      const data = await clinicAPI.initTenantSubscriptionPayment();
      if (data.payment_url) {
        window.open(data.payment_url, "_blank", "noopener,noreferrer");
        toast.success("Страница оплаты открыта");
      }
    } catch (e) {
      toast.error(e.response?.data?.error || "Онлайн-оплата недоступна");
    } finally {
      setPayBusy(false);
    }
  };

  return (
    <HubScreen
      title="Расширенные настройки"
      onBack={() => navigate("/manage/app/profile")}
      backLabel="Профиль"
    >
      <HubFormCard title="Мои данные">
        <Field label="Имя">
          <Input
            value={form.full_name}
            onChange={(e) => setForm({ ...form, full_name: e.target.value })}
          />
        </Field>
        <Field label="Телефон">
          <Input
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
          />
        </Field>
        <Field label="Email">
          <Input value={profile?.email || ""} readOnly />
        </Field>
        <Button type="button" onClick={saveProfile} disabled={saving}>
          {saving ? "Сохранение…" : "Сохранить"}
        </Button>
      </HubFormCard>

      {billing ? (
        <BillingDetailsCard
          billing={billing}
          payBusy={payBusy}
          onPay={paySubscription}
          trialDays={TRIAL_DAYS}
          formatRub={formatRub}
          formatDateRu={formatDateRu}
        />
      ) : null}

      <HubFormCard title="Уведомления">
        <AdminPushBlock />
      </HubFormCard>

      <HubFormCard title="Пароль">
        <Field label="Текущий пароль">
          <Input
            type="password"
            value={pwd.current}
            onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
          />
        </Field>
        <Field label="Новый пароль">
          <Input
            type="password"
            value={pwd.next}
            onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
          />
        </Field>
        <Button
          type="button"
          onClick={savePassword}
          disabled={pwdSaving}
          variant="secondary"
        >
          {pwdSaving ? "…" : "Сменить пароль"}
        </Button>
      </HubFormCard>

      <button
        type="button"
        className={aa.staffProfileMenuItem}
        onClick={() => navigate("/manage/app/settings")}
      >
        <span className={aa.staffProfileMenuIcon} aria-hidden>
          ⚙️
        </span>
        <span className={aa.staffProfileMenuText}>
          <strong>Настройки клуба</strong>
          <span>Портал, брендинг, импорт данных</span>
        </span>
        <span className={aa.staffProfileMenuChevron} aria-hidden />
      </button>
    </HubScreen>
  );
}
